import { useState, memo, lazy, Suspense, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Lock, ArrowRight, Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { getBrowserLocalTimestamp } from "@shared/browser-local-time";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { hasSessionExpiredNotice, isWorkspaceReturnPath, SESSION_EXPIRED_QUERY_PARAM, SESSION_EXPIRATION_MESSAGE } from "@shared/session-expiration";

// Lazy load ParticleBackground to defer rendering until needed
// Global maintenance mode enabled
const ParticleBackground = lazy(() => 
  import("@/components/ParticleBackground").then(m => ({ default: m.ParticleBackground }))
);

// Memoize the login form to prevent unnecessary re-renders
const LoginForm = memo(({ 
  username, 
  setUsername, 
  password, 
  setPassword, 
  loading, 
  loginMutation, 
  handleLogin,
  navigate,
  forgotPasswordPath,
}: {
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  loading: boolean;
  loginMutation: any;
  handleLogin: (e: React.FormEvent) => void;
  navigate: (path: string) => void;
  forgotPasswordPath: string;
}) => (
  <form onSubmit={handleLogin} className="space-y-4">
    <div>
      <h2 className="text-2xl font-black text-gray-900 mb-2">Welcome Back</h2>
      <p className="text-sm text-gray-700 mb-6 font-medium">
        Sign in to access your dashboards.
      </p>

    </div>

    <div className="space-y-2">
      <Label htmlFor="username" className="text-sm font-bold text-gray-800">
        Username
      </Label>
      <div className="relative">
        <Input
          id="username"
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={loading}
          autoComplete="off"
          className="bg-white/60 border border-gray-300 text-gray-900 font-medium placeholder:text-gray-600 focus:bg-white/80 focus:border-gray-400 transition-colors"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="password" className="text-sm font-bold text-gray-800">
        Password
      </Label>
      <div className="relative">
        <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-600" />
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          autoComplete="off"
          className="pl-10 bg-white/60 border border-gray-300 text-gray-900 font-medium placeholder:text-gray-600 focus:bg-white/80 focus:border-gray-400 transition-colors"
        />
      </div>
    </div>

    <Button
      type="submit"
      disabled={loading || loginMutation.isPending}
      className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors cursor-pointer"
    >
      {loading || loginMutation.isPending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <ArrowRight className="w-4 h-4" />
      )}
      {loading || loginMutation.isPending ? "Signing in..." : "Sign In"}
    </Button>

    <div className="flex gap-2 text-sm">
      <button
        type="button"
        onClick={() => navigate(forgotPasswordPath)}
        className="w-full rounded-md bg-secondary py-2 text-center font-semibold text-primary transition-colors hover:bg-accent hover:text-accent-foreground"
      >
        Forgot Password?
      </button>
    </div>
  </form>
));

LoginForm.displayName = "LoginForm";

export default function LoginPage() {
  const [workspaceChoices, setWorkspaceChoices] = useState<Array<{
    id: string;
    kind: "legacy" | "tenant" | "repair-center";
    name: string;
    slug: string | null;
    path: string;
    membershipRole: string | null;
    plan: string | null;
  }>>([]);
  const [mode, setMode] = useState<"login">("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showParticles, setShowParticles] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [showSessionExpiredNotice, setShowSessionExpiredNotice] = useState(() =>
    typeof window !== "undefined" && hasSessionExpiredNotice(window.location.search),
  );

  const [loading, setLoading] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('Site is currently under maintenance. Please check back within 1 to 2 hours.');
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();
  const requestedReturnPath = typeof window === "undefined"
    ? null
    : (new URLSearchParams(window.location.search).get("return") || new URLSearchParams(window.location.search).get("returnTo"));
  const safeReturnPath = isWorkspaceReturnPath(requestedReturnPath) ? requestedReturnPath : "/app";
  const tenantWorkspaceSlug = safeReturnPath.match(/^\/tenant\/([^/?#]+)/)?.[1];
  const forgotPasswordPath = `/forgot-password?return=${encodeURIComponent(safeReturnPath)}`;
  const tenantWorkspace = trpc.saas.getWorkspaceBySlug.useQuery(
    { slug: tenantWorkspaceSlug || "tenant-maintenance-disabled" },
    { enabled: Boolean(tenantWorkspaceSlug), retry: false, staleTime: 15_000 },
  );
  const tenantMaintenanceActive = tenantWorkspace.data?.maintenanceEnabled === 1;
  const tenantMaintenanceMessage = tenantWorkspace.data?.maintenanceMessage || "This organization workspace is currently under maintenance. Please check back shortly.";
  const dismissSessionExpiredNotice = () => {
    setShowSessionExpiredNotice(false);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.delete(SESSION_EXPIRED_QUERY_PARAM);
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const loginMutation = trpc.auth.loginWithPassword.useMutation({
    retry: false,
  });
  const recordTenantLoginMutation = trpc.saas.recordTenantLogin.useMutation({
    retry: false,
  });

  const continueToWorkspace = async (workspace: typeof workspaceChoices[number]) => {
    if (workspace.kind === "tenant" && workspace.slug) {
      try {
        const { timezone, localDateTime } = getBrowserLocalTimestamp();
        await recordTenantLoginMutation.mutateAsync({
          slug: workspace.slug,
          timezone,
          localDateTime,
        });
      } catch (error) {
        // Keep navigation available if audit storage is temporarily unavailable;
        // the server-side login event is still preserved for direct/single-tenant
        // routes, and this failure should never block a valid sign-in.
        console.error("[Auth] Failed to record tenant workspace login:", error);
      }
    }
    navigate(workspace.path);
  };

  // The client sign-in route is also used as an installed/shortcut entry point.
  // Set a route-specific, cache-busted favicon so older shortcut metadata cannot
  // keep displaying the legacy clock icon in the browser tab.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const favicon = document.querySelector<HTMLLinkElement>("#tracklabs-client-login-favicon")
      ?? document.createElement("link");
    favicon.id = "tracklabs-client-login-favicon";
    favicon.rel = "icon";
    favicon.type = "image/x-icon";
    favicon.href = "/tracklabs-favicon.ico?client-login=v2";
    document.head.appendChild(favicon);
  }, []);

  // Load maintenance mode from database on mount - shared for all users
  useEffect(() => {
    const loadMaintenanceStatus = async () => {
      try {
        const response = await fetch('/api/maintenance-status');
        if (response.ok) {
          const data = await response.json();
          setMaintenanceMode(data.isEnabled || false);
          setMaintenanceMessage(data.message || 'Site is currently under maintenance. Please check back within 1 to 2 hours.');
        }
      } catch (error) {
        console.error('Failed to load maintenance status:', error);
      }
    };
    loadMaintenanceStatus();
  }, []);

  // Defer ParticleBackground rendering to improve FCP and Speed Index
  useEffect(() => {
    // Use requestIdleCallback if available, otherwise use setTimeout
    const timeoutId = (typeof requestIdleCallback !== 'undefined')
      ? requestIdleCallback(() => setShowParticles(true), { timeout: 2000 })
      : setTimeout(() => setShowParticles(true), 500);

    return () => {
      if (typeof requestIdleCallback !== 'undefined' && typeof timeoutId === 'number') {
        cancelIdleCallback(timeoutId);
      } else {
        clearTimeout(timeoutId as NodeJS.Timeout);
      }
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Username and password required");
      return;
    }

    // Prevent double submission
    if (loading) return;
    setLoading(true);

    try {
      const { timezone, localDateTime } = getBrowserLocalTimestamp();
      const result = await loginMutation.mutateAsync({ username, password, workspaceSlug: tenantWorkspaceSlug, timezone, localDateTime });
      if (result.success) {
        // Protected routes cache auth.me aggressively to avoid tab-mount churn.
        // Populate that cache immediately so a successful login cannot be
        // bounced back to /login by an earlier null auth.me result.
        utils.auth.me.setData(undefined, result.user as any);
        void utils.auth.me.refetch();
        toast.success("Login successful!");
        if ((result.user as any)?.mustChangePassword) {
          const setupReturn = requestedReturnPath || result.destinationPath || "/app";
          navigate(`/first-login?return=${encodeURIComponent(setupReturn)}`);
          return;
        }
        // A direct workspace link stays direct. Universal logins are routed by
        // the server-authorized directory returned with the successful login.
        if (requestedReturnPath) {
          navigate(safeReturnPath);
        } else if (result.destinationPath) {
          navigate(result.destinationPath);
        } else if (result.requiresWorkspaceChoice && result.workspaces?.length) {
          setWorkspaceChoices(result.workspaces);
          setLoading(false);
        } else {
          setLoading(false);
          toast.error("No authorized workspace is available for this account.");
        }
      } else {
        toast.error("Login failed");
        setLoading(false);
      }
    } catch (error: any) {
      const msg = error?.message || "Invalid username or password";
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center overflow-hidden relative">
      <AlertDialog open={showSessionExpiredNotice} onOpenChange={setShowSessionExpiredNotice}>
        <AlertDialogContent className="max-w-md border-2 border-amber-300 bg-card shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Session Expired</AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-foreground">
              {SESSION_EXPIRATION_MESSAGE}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={dismissSessionExpiredNotice}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Particle Background - Deferred rendering for better FCP and Speed Index */}
      {showParticles && (
        <Suspense fallback={null}>
          <ParticleBackground variant="mint" particleCount={50} />
        </Suspense>
      )}

      {/* Content - Rendered immediately for better FCP */}
      <div className="relative z-20 w-full max-w-md px-4">
        {/* Translucent Login Card */}
        <div className="bg-white/75 backdrop-blur-xl rounded-3xl border border-white/60 shadow-2xl p-8 relative">
          {/* Logo sits inside the card with balanced top and bottom spacing. */}
          <div className="mb-7 flex w-full justify-center pt-1">
            <img
              src="https://tracklabs-2rqxgdhv.manus.space/manus-storage/track-labs-logo-no-tagline_b38a3502.png"
              alt="TrackLab Logo"
              width={256}
              height={64}
              className="h-auto w-64 max-w-full object-contain drop-shadow-lg"
              loading="eager"
              fetchPriority="high"
            />
          </div>

          {/* Maintenance Mode Message */}
          {maintenanceMode && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-500 rounded-lg">
              <p className="text-red-700 font-bold text-center">{maintenanceMessage}</p>
            </div>
          )}

          {tenantMaintenanceActive && (
            <div className="mb-6 rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
              <p className="font-bold text-amber-950">{tenantWorkspace.data?.name || "This organization workspace"} is under maintenance</p>
              <p className="mt-1 text-sm leading-5 text-amber-900">{tenantMaintenanceMessage}</p>
              <p className="mt-2 text-xs font-semibold text-amber-800">Only the designated original Track Labs workspace owner can sign in while maintenance is active.</p>
            </div>
          )}

          {workspaceChoices.length > 0 ? (
            <section aria-label="Choose an authorized workspace">
              <div className="mb-6">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-primary">Track Labs</p>
                <h2 className="mt-2 text-2xl font-black text-gray-900">Choose your workspace</h2>
                <p className="mt-2 text-sm font-medium leading-6 text-gray-700">This account is authorized for more than one workspace. Select where to continue.</p>
              </div>
              <div className="space-y-3">
                {workspaceChoices.map((workspace) => (
                  <button
                    key={workspace.id}
                    type="button"
                    onClick={() => void continueToWorkspace(workspace)}
                    className="group flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-white hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-5 w-5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-black text-gray-900">{workspace.name}</span>
                      <span className="mt-0.5 block text-xs font-semibold capitalize text-gray-600">{workspace.kind === "legacy" ? "Track Labs platform" : workspace.kind === "repair-center" ? "Protected operations workspace" : `${workspace.membershipRole || "member"} workspace`}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform duration-200 group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
            </section>
          ) : mode === "login" && (
            <LoginForm
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              loading={loading}
              loginMutation={loginMutation}
              handleLogin={handleLogin}
              navigate={navigate}
              forgotPasswordPath={forgotPasswordPath}
            />
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-700 font-medium mt-6">
          Track Labs © 2026
        </p>
        <nav aria-label="Legal" className="mt-2 flex justify-center gap-4 text-xs font-semibold text-gray-600">
          <a href="/terms-of-use" className="transition-colors hover:text-gray-950 hover:underline">Terms of Use</a>
          <a href="/copyright" className="transition-colors hover:text-gray-950 hover:underline">Copyright</a>
        </nav>
      </div>
    </div>
  );
}
